import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Duration } from "luxon";

// classes
import Flight from "classes/flight";
import Route from "classes/route";

// hooks and utilities
import { getAirportById } from "./useAirport";
import { useLanguage } from "lang/Translate";
import useAircraft from "./useAircraft";

/**
 * Custom hook that retrieves information about an aircraft flight.
 *
 * @param {string} aircraftIdent - The identifier of the aircraft.
 * @returns {Object|null} - An object containing information about the aircraft flight, or null if the flight or aircraft identifier is invalid.
 * @property {string} origin - The origin airport of the flight.
 * @property {string} destination - The destination airport of the flight.
 * @property {string} originCode - The IATA or ICAO code of the origin airport.
 * @property {string} destinationCode - The IATA or ICAO code of the destination airport.
 * @property {number} distance - The distance of the flight in kilometers.
 * @property {number} flightTime - The estimated flight time in seconds.
 * @property {string} formattedFlightTime - The formatted flight time in the format "hh:mm h".
 */
const useAircraftFlight = aircraftIdent => {
	const [[origin, destination], setAirports] = useState([]);

	const aircraft = useAircraft(aircraftIdent);
	const aircraftList = useSelector(state => state.aircraftList);
	const airportList = useSelector(state => state.airports);
	const currLanguage = useLanguage();

	const flight = Flight.get(aircraft?.currentFlight);
	const route = Route.get(aircraft?.route);

	useEffect(() => {
		if (!flight) return setAirports([]);
		if (!aircraft?.location && !aircraft?.departedFrom) return;
		setAirports(flight.getAirports(aircraft?.location || aircraft?.departedFrom) || []);
	}, [flight, aircraft?.location, aircraft?.departedFrom]);

	const getAircraftManufacturer = id => {
		let result = "";
		Object.entries(aircraftList.passenger).forEach(([key, value], i) => {
			if (i === 0) return true;
			const containsAircraft = value.filter(aircraft => aircraft.id === id).length > 0;
			if (containsAircraft) result = key;
		});

		return result;
	};

	const getFlightTime = distance => {
		const speed = aircraft.specs.speed[0]; // km/h
		const flightTime = (distance / speed / 1.5) * 3600; // sek
		return flightTime;
	};

	if (aircraft?.status === "delivery" && !route) {
		console.error("Cannot provide details about the flight because the route is undefined!");
		return null;
	}

	if (!flight && aircraft?.status !== "delivery") return null;
	if (!aircraftIdent || (!aircraft?.currentFlight && aircraft?.status !== "delivery")) return null;
	if (aircraft.status === "delivery") {
		const originLabel = `${currLanguage === "PL" ? "Fabryka " : ""}
        ${getAircraftManufacturer(aircraft.id)}${currLanguage !== "PL" ? " factory" : ""}`;

		const origin = getAirportById(airportList, route.from);
		const destination = getAirportById(airportList, route.to);

		return {
			origin: originLabel,
			destination: `${destination.name} (${destination.iata_code || destination.icao_code})`,
			originCode: origin.iata_code || origin.icao_code,
			destinationCode: destination.iata_code || destination.icao_code,
			distance: route.distance,
			flightTime: getFlightTime(route.distance),
			formattedFlightTime: Duration.fromObject({ seconds: getFlightTime(route.distance) }).toFormat("hh:mm") + " h"
		};
	}

	return {
		origin: `${origin?.name} (${origin?.iata_code || origin?.icao_code})`,
		destination: `${destination?.name} (${destination?.iata_code || destination?.icao_code})`,
		originCode: origin?.iata_code || origin?.icao_code,
		destinationCode: destination?.iata_code || destination?.icao_code,
		distance: flight?.distance,
		flightTime: getFlightTime(flight.distance),
		formattedFlightTime: Duration.fromObject({ seconds: getFlightTime(flight.distance) }).toFormat("hh:mm") + " h"
	};
};

export default useAircraftFlight;
